import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import LinearProgress from '@material-ui/core/LinearProgress';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MuiSelect from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import AddIcon from '@material-ui/icons/Add';
import { makeStyles } from '@material-ui/core/styles';
import { useErrorToast } from '../../shared/toast/ToastProvider';
import DataTable from '../../shared/components/DataTable';
import { useProjectDataSourcesListingApi } from '../hooks';
import DataSourceStatusChip, { DATA_SOURCE_STATUSES } from '../DataSourceStatus';

const useStyles = makeStyles((theme) => ({
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        padding: theme.spacing(1, 0, 2, 0),
        flexWrap: 'wrap',
    },
    filterControl: {
        minWidth: 180,
    },
    searchField: {
        minWidth: 240,
    },
    spacer: {
        flex: 1,
    },
}));

const statusSortOrder = Object.fromEntries(
    DATA_SOURCE_STATUSES.map(({ key }, i) => [key, i])
);

// Normalise a status string from the API (e.g. "Not active", "Not_Active") to
// the underscore-lowercase key used by DATA_SOURCE_STATUSES (e.g. "not_active").
const normaliseStatusKey = (s) => (s ? s.toLowerCase().replace(/[\s-]+/g, '_') : '');

const columns = [
    {
        id: 'code',
        label: 'Data Source Code',
        minWidth: 170,
        align: 'left',
        sortable: true,
    },
    {
        id: 'connection',
        label: 'Connection Code',
        minWidth: 170,
        sortable: true,
    },
    {
        id: 'provider',
        label: 'Provider Code',
        minWidth: 170,
        sortable: true,
    },
    {
        id: 'status',
        label: 'Status',
        minWidth: 140,
        sortable: true,
        sortValue: (row) => statusSortOrder[normaliseStatusKey(row.status)] ?? 999,
    },
];

const ProjectDataSourceList = ({ add, details }) => {
    const classes = useStyles();
    const { projectCode } = useParams();
    const [{
        isLoading,
        isError,
        data
    }] = useProjectDataSourcesListingApi(projectCode);

    const [statusFilter, setStatusFilter] = useState('active');
    const [searchTerm, setSearchTerm] = useState('');

    useErrorToast(isError, 'Failed to load data sources');

    const handleDetails = (row) => details(row);

    const handleStatusFilter = (e) => {
        setStatusFilter(e.target.value);
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const processedRows = useMemo(() => {
        let rows = data || [];

        const isNullStatus = (s) => !s || s.toLowerCase() === 'null';
        if (statusFilter === '__null__') {
            rows = rows.filter((r) => isNullStatus(r.status));
        } else if (statusFilter) {
            rows = rows.filter((r) => (
                !isNullStatus(r.status)
                && normaliseStatusKey(r.status) === statusFilter
            ));
        }

        if (searchTerm.trim()) {
            const term = searchTerm.trim().toLowerCase();
            rows = rows.filter((r) => (
                (r.code && r.code.toLowerCase().includes(term))
                || (r.connection && r.connection.toLowerCase().includes(term))
                || (r.provider && r.provider.toLowerCase().includes(term))
            ));
        }

        return rows;
    }, [data, statusFilter, searchTerm]);

    const cellRenderer = (column, value) => {
        if (column.id === 'status') {
            return <DataSourceStatusChip status={value} />;
        }
        return value;
    };

    return (
        <>
            {isLoading && <LinearProgress role="progressbar" />}
            <Box className={classes.toolbar}>
                <TextField
                    className={classes.searchField}
                    label="Search"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={handleSearch}
                    placeholder="Code, connection or provider…"
                />
                <FormControl className={classes.filterControl} size="small" variant="outlined">
                    <InputLabel id="status-filter-label">Filter by Status</InputLabel>
                    <MuiSelect
                        labelId="status-filter-label"
                        value={statusFilter}
                        onChange={handleStatusFilter}
                        label="Filter by Status"
                    >
                        <MenuItem value=""><em>All statuses</em></MenuItem>
                        <MenuItem value="__null__"><em>Not set</em></MenuItem>
                        {DATA_SOURCE_STATUSES.map(({ key, value }) => (
                            <MenuItem key={key} value={key}>
                                {value}
                            </MenuItem>
                        ))}
                    </MuiSelect>
                </FormControl>
                <div className={classes.spacer} />
                {add && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={add}
                    >
                        Add New
                    </Button>
                )}
            </Box>
            <DataTable
                columns={columns}
                rows={processedRows}
                add={add}
                details={handleDetails}
                tableName="project-datasources"
                cellRenderer={cellRenderer}
                disablePagination
                withoutAddButton
            />
        </>
    );
};

export default ProjectDataSourceList;
